import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiShieldCheck,
  HiKey,
  HiCheckCircle,
  HiXMark,
  HiLockClosed,
  HiSparkles,
} from 'react-icons/hi2';
import { useAuth } from '../../context/AuthContext';
import {
  getUserSettings,
  toggle2FA,
  generate2FASecret,
  toggleLoginOtp,
} from '../../services/settingsService';
import { useToast } from '../../context/ToastContext';
import { DeleteConfirmation } from '../ui/DeleteConfirmation';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 12,
    },
  },
};

const pulseVariants = {
  initial: { scale: 1 },
  pulse: {
    scale: [1, 1.05, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

export default function TwoFactorSettings() {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEnableOtpConfirm, setShowEnableOtpConfirm] = useState(false);
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [otpSaving, setOtpSaving] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadSettings();
    }
  }, [currentUser]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await getUserSettings(currentUser.uid);
      setEnabled(data.twoFactorEnabled || false);
      setOtpEnabled(data.loginOtpEnabled || false);
    } catch (error) {
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = () => {
    const { secret: newSecret, backupCodes: newCodes } = generate2FASecret();
    setSecret(newSecret);
    setBackupCodes(newCodes);

    const issuer = 'BusinessHub';
    const accountName = currentUser.email;
    const otpauthUrl = `otpauth://totp/${issuer}:${accountName}?secret=${newSecret}&issuer=${issuer}`;
    setQrCodeUrl(
      `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpauthUrl)}`
    );

    setShowSetup(true);
  };

  const handleVerifyAndEnable = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    try {
      setSaving(true);
      await toggle2FA({
        uid: currentUser.uid,
        enabled: true,
        secret,
        backupCodes,
      });

      setEnabled(true);
      setShowSetup(false);
      setVerificationCode('');
      toast.success('Two-factor authentication enabled');
    } catch (error) {
      toast.error('Failed to enable 2FA');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleOtp = async () => {
    const next = !otpEnabled;

    if (next) {
      setShowEnableOtpConfirm(true);
      return;
    }

    await performOtpToggle(false);
  };

  const handleConfirmEnableOtp = async () => {
    setShowEnableOtpConfirm(false);
    await performOtpToggle(true);
  };

  const performOtpToggle = async next => {
    try {
      setOtpSaving(true);
      await toggleLoginOtp({
        uid: currentUser.uid,
        enabled: next,
      });
      setOtpEnabled(next);
      toast.success(
        next
          ? 'Login OTP enabled. A verification code will be sent to your email on each sign-in.'
          : 'Login OTP disabled.'
      );
    } catch (error) {
      toast.error('Failed to update login OTP setting');
    } finally {
      setOtpSaving(false);
    }
  };

  const handleDisable2FAClick = () => {
    setShowDeleteModal(true);
  };

  const handleDisable2FA = async () => {
    try {
      setSaving(true);
      await toggle2FA({
        uid: currentUser.uid,
        enabled: false,
      });

      setEnabled(false);
      setShowDeleteModal(false);
      toast.success('Two-factor authentication disabled');
    } catch (error) {
      toast.error('Failed to disable 2FA', 'Error');
    } finally {
      setSaving(false);
    }
  };

  const downloadBackupCodes = () => {
    const content = `Business Hub - Backup Codes\n\nGenerated: ${new Date().toLocaleString()}\nAccount: ${currentUser.email}\n\n${backupCodes.join('\n')}\n\nKeep these codes safe. Each code can only be used once.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '2fa-backup-codes.txt';
    a.click();
  };

  if (loading) {
    return (
      <div className="flex min-h-[500px] items-center justify-center rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-12 shadow-2xl shadow-slate-200/50">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="text-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
            className="mx-auto mb-4 h-16 w-16 rounded-full border-4 border-blue-600 border-t-transparent shadow-lg"
          />
          <p className="text-sm font-medium text-slate-600">Loading security settings...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl shadow-slate-200/50"
    >
      {/* Premium Header with Gradient Background */}
      <div className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 px-8 py-10">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[length:24px_24px]" />
        </div>

        <motion.div variants={itemVariants} className="relative flex items-start gap-6">
          <motion.div
            variants={pulseVariants}
            initial="initial"
            animate={enabled ? 'pulse' : 'initial'}
            className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-2xl shadow-blue-500/30"
          >
            <HiShieldCheck className="h-8 w-8 text-white" />
          </motion.div>

          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-3xl font-bold tracking-tight text-white">
                Two-Factor Authentication
              </h2>
              {enabled && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="flex items-center gap-1.5 rounded-full bg-green-500/20 px-3 py-1 backdrop-blur-sm"
                >
                  <HiSparkles className="h-4 w-4 text-green-400" />
                  <span className="text-xs font-semibold text-green-400">Active</span>
                </motion.div>
              )}
            </div>
            <p className="mt-2 text-base text-blue-200">
              Enterprise-grade security for your account with military-grade encryption
            </p>
          </div>
        </motion.div>
      </div>

      {/* Content Area */}
      <div className="p-8 sm:p-10">
        {/* Status Card */}
        <motion.div variants={itemVariants}>
          <div
            className={`group relative overflow-hidden rounded-2xl border-2 p-8 transition-all duration-300 ${
              enabled
                ? 'border-green-300 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 shadow-lg shadow-green-200/50'
                : 'border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 hover:shadow-lg'
            }`}
          >
            {/* Decorative Corner Elements */}
            <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-gradient-to-br from-blue-500/10 to-indigo-500/10 blur-2xl" />
            <div className="absolute -bottom-8 -left-8 h-32 w-32 rounded-full bg-gradient-to-br from-purple-500/10 to-pink-500/10 blur-2xl" />

            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-5">
                <motion.div
                  animate={enabled ? { scale: [1, 1.2, 1] } : {}}
                  transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 3 }}
                  className={`flex h-16 w-16 items-center justify-center rounded-xl shadow-lg ${
                    enabled
                      ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-green-500/30'
                      : 'bg-gradient-to-br from-slate-400 to-slate-500'
                  }`}
                >
                  {enabled ? (
                    <HiCheckCircle className="h-9 w-9 text-white" />
                  ) : (
                    <HiLockClosed className="h-8 w-8 text-white" />
                  )}
                </motion.div>

                <div>
                  <div className="flex items-center gap-3">
                    <p className="text-2xl font-bold text-slate-900">
                      {enabled ? 'Protected' : 'Not Protected'}
                    </p>
                  </div>
                  <p className="mt-1 text-sm font-medium text-slate-600">
                    {enabled
                      ? 'Your account is secured with authenticator-based 2FA'
                      : 'Add an extra security layer to protect your account'}
                  </p>
                </div>
              </div>

              {enabled && (
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleDisable2FAClick}
                  disabled={saving}
                  className="group/btn relative overflow-hidden rounded-xl border-2 border-red-200 bg-white px-6 py-3 font-semibold text-red-600 shadow-lg transition-all hover:border-red-300 hover:shadow-xl disabled:opacity-50"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <HiXMark className="h-5 w-5" />
                    Disable 2FA
                  </span>
                  <div className="absolute inset-0 -z-0 bg-gradient-to-r from-red-50 to-rose-50 opacity-0 transition-opacity group-hover/btn:opacity-100" />
                </motion.button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Login OTP Card */}
        <motion.div variants={itemVariants} className="mt-6">
          <div className="group relative overflow-hidden rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 p-6 shadow-lg transition-all hover:shadow-xl">
            {/* Decorative Elements */}
            <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-indigo-400/20 to-blue-400/20 blur-2xl" />

            <div className="relative flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-600 to-blue-600 shadow-lg shadow-indigo-500/30">
                    <svg
                      className="h-5 w-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Email-Based Login OTP</h3>
                </div>

                <p className="mt-3 text-sm leading-relaxed text-slate-700">
                  Receive a one-time verification code via email after entering your password. This
                  adds an additional security checkpoint to your login process.
                </p>

                <div className="mt-4 flex items-start gap-2 rounded-lg bg-white/60 p-3 backdrop-blur-sm">
                  <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100">
                    <svg
                      className="h-3.5 w-3.5 text-indigo-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-xs font-medium text-slate-600">
                    Independent from authenticator 2FA • Can be used together or separately
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-stretch gap-3 sm:flex-shrink-0 sm:items-end">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={handleToggleOtp}
                  disabled={otpSaving}
                  className={`relative overflow-hidden rounded-xl px-6 py-3 font-bold shadow-lg transition-all ${
                    otpEnabled
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-green-500/30 hover:from-green-600 hover:to-emerald-700'
                      : 'bg-gradient-to-r from-slate-600 to-slate-700 text-white shadow-slate-500/30 hover:from-slate-700 hover:to-slate-800'
                  }`}
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {otpEnabled ? (
                      <>
                        <HiCheckCircle className="h-5 w-5" />
                        OTP Enabled
                      </>
                    ) : (
                      <>
                        <HiLockClosed className="h-5 w-5" />
                        Enable OTP
                      </>
                    )}
                  </span>
                </motion.button>

                <div
                  className={`rounded-full px-4 py-1.5 text-center text-xs font-bold sm:text-left ${
                    otpEnabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {otpEnabled ? '✓ Email verification active' : 'Password-only login'}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Main Content Area */}
        <AnimatePresence mode="wait">
          {!enabled && !showSetup && (
            <motion.div
              key="enable-prompt"
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, scale: 0.95 }}
              className="mt-8"
            >
              <div className="rounded-2xl border-2 border-slate-200 bg-gradient-to-br from-white to-slate-50 p-8 shadow-lg">
                <div className="mx-auto max-w-2xl text-center">
                  <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-2xl shadow-blue-500/30">
                    <HiShieldCheck className="h-10 w-10 text-white" />
                  </div>

                  <h3 className="mb-4 text-2xl font-bold text-slate-900">
                    Enhance Your Account Security
                  </h3>

                  <p className="mb-8 text-base leading-relaxed text-slate-600">
                    Two-factor authentication provides an additional layer of protection by
                    requiring both your password and a time-based verification code from your mobile
                    authenticator app. Even if someone discovers your password, they won't be able
                    to access your account without the second factor.
                  </p>

                  <motion.button
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleEnable2FA}
                    className="hover:shadow-3xl group relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-10 py-4 text-lg font-bold text-white shadow-2xl shadow-blue-500/30 transition-all"
                  >
                    <span className="relative z-10 flex items-center justify-center gap-3">
                      <HiShieldCheck className="h-6 w-6" />
                      Enable Two-Factor Authentication
                      <svg
                        className="h-5 w-5 transition-transform group-hover:translate-x-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 7l5 5m0 0l-5 5m5-5H6"
                        />
                      </svg>
                    </span>
                    <div className="absolute inset-0 -z-0 bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 opacity-0 transition-opacity group-hover:opacity-100" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}

          {showSetup && (
            <motion.div
              key="setup-form"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="mt-8 space-y-6"
            >
              {/* Step 1: Scan QR Code */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="group relative overflow-hidden rounded-2xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-50 p-8 shadow-lg transition-all hover:shadow-xl"
              >
                <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-gradient-to-br from-blue-400/20 to-indigo-400/20 blur-3xl" />

                <div className="relative flex gap-6">
                  <div className="flex-shrink-0">
                    <motion.div
                      animate={{ rotate: [0, 5, -5, 0] }}
                      transition={{ duration: 4, repeat: Infinity }}
                      className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 font-bold text-white shadow-xl shadow-blue-500/30"
                    >
                      <span className="text-2xl">1</span>
                    </motion.div>
                  </div>

                  <div className="flex-1">
                    <h3 className="mb-2 text-xl font-bold text-slate-900">Scan QR Code</h3>
                    <p className="mb-6 text-sm leading-relaxed text-slate-700">
                      Use your authenticator app (Google Authenticator, Microsoft Authenticator,
                      Authy, 1Password, etc.) to scan this code:
                    </p>

                    {qrCodeUrl && (
                      <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="mb-6 flex justify-center"
                      >
                        <div className="rounded-2xl bg-white p-6 shadow-2xl ring-4 ring-blue-100">
                          <img src={qrCodeUrl} alt="2FA QR Code" className="h-48 w-48 rounded-lg" />
                        </div>
                      </motion.div>
                    )}

                    <div className="rounded-xl bg-white/80 p-4 backdrop-blur-sm">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Or enter manually:
                      </p>
                      <div className="flex items-center gap-3">
                        <code className="flex-1 rounded-lg bg-slate-100 px-4 py-3 font-mono text-sm font-bold text-slate-900">
                          {secret}
                        </code>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            navigator.clipboard.writeText(secret);
                            toast.success('Secret copied to clipboard');
                          }}
                          className="rounded-lg bg-blue-600 p-3 text-white shadow-lg hover:bg-blue-700"
                        >
                          <svg
                            className="h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Step 2: Verify */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="group relative overflow-hidden rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 via-fuchsia-50 to-purple-50 p-8 shadow-lg transition-all hover:shadow-xl"
              >
                <div className="absolute -left-12 -top-12 h-40 w-40 rounded-full bg-gradient-to-br from-purple-400/20 to-fuchsia-400/20 blur-3xl" />

                <div className="relative flex gap-6">
                  <div className="flex-shrink-0">
                    <motion.div
                      animate={{ rotate: [0, -5, 5, 0] }}
                      transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
                      className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-fuchsia-600 font-bold text-white shadow-xl shadow-purple-500/30"
                    >
                      <span className="text-2xl">2</span>
                    </motion.div>
                  </div>

                  <div className="flex-1">
                    <h3 className="mb-2 text-xl font-bold text-slate-900">Verify Code</h3>
                    <p className="mb-6 text-sm leading-relaxed text-slate-700">
                      Enter the 6-digit verification code displayed in your authenticator app:
                    </p>

                    <div className="relative">
                      <input
                        type="text"
                        value={verificationCode}
                        onChange={e =>
                          setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                        }
                        className="border-3 w-full rounded-xl border-purple-300 bg-white px-6 py-5 text-center text-3xl font-bold tracking-[0.5em] text-slate-900 shadow-lg transition-all placeholder:text-slate-300 focus:border-purple-500 focus:outline-none focus:ring-4 focus:ring-purple-500/20"
                        placeholder="000000"
                        maxLength={6}
                      />
                      {verificationCode.length === 6 && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute right-4 top-1/2 -translate-y-1/2"
                        >
                          <HiCheckCircle className="h-8 w-8 text-green-500" />
                        </motion.div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Step 3: Backup Codes */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="group relative overflow-hidden rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50 p-8 shadow-lg transition-all hover:shadow-xl"
              >
                <div className="absolute -bottom-12 -right-12 h-40 w-40 rounded-full bg-gradient-to-br from-amber-400/20 to-orange-400/20 blur-3xl" />

                <div className="relative flex gap-6">
                  <div className="flex-shrink-0">
                    <motion.div
                      animate={{ rotate: [0, 5, -5, 0] }}
                      transition={{ duration: 4, repeat: Infinity, delay: 1 }}
                      className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-amber-600 to-orange-600 font-bold text-white shadow-xl shadow-amber-500/30"
                    >
                      <span className="text-2xl">3</span>
                    </motion.div>
                  </div>

                  <div className="flex-1">
                    <h3 className="mb-2 text-xl font-bold text-slate-900">Save Backup Codes</h3>
                    <p className="mb-6 text-sm leading-relaxed text-slate-700">
                      Store these emergency codes in a secure location. Use them to access your
                      account if you lose access to your authenticator device.
                    </p>

                    <div className="mb-4 grid grid-cols-2 gap-3">
                      {backupCodes.map((code, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 + index * 0.05 }}
                          className="group/code relative overflow-hidden rounded-lg bg-white p-4 shadow-md transition-all hover:shadow-lg"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-amber-400/10 to-orange-400/10 opacity-0 transition-opacity group-hover/code:opacity-100" />
                          <div className="relative flex items-center justify-between">
                            <span className="font-mono text-sm font-bold text-slate-900">
                              {code}
                            </span>
                            <span className="text-xs font-semibold text-slate-400">
                              #{index + 1}
                            </span>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={downloadBackupCodes}
                      className="group/btn flex items-center gap-3 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-3 font-bold text-white shadow-lg transition-all hover:from-amber-700 hover:to-orange-700 hover:shadow-xl"
                    >
                      <HiKey className="h-5 w-5" />
                      Download Backup Codes
                      <svg
                        className="h-4 w-4 transition-transform group-hover/btn:translate-y-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </motion.button>
                  </div>
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex justify-end gap-4 border-t-2 border-slate-200 pt-8"
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setShowSetup(false);
                    setVerificationCode('');
                  }}
                  className="rounded-xl border-2 border-slate-300 bg-white px-8 py-3 font-bold text-slate-700 shadow-lg transition-all hover:border-slate-400 hover:bg-slate-50 hover:shadow-xl"
                >
                  Cancel Setup
                </motion.button>

                <motion.button
                  whileHover={{ scale: verificationCode.length === 6 ? 1.02 : 1 }}
                  whileTap={{ scale: verificationCode.length === 6 ? 0.98 : 1 }}
                  onClick={handleVerifyAndEnable}
                  disabled={saving || verificationCode.length !== 6}
                  className="group relative overflow-hidden rounded-xl bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 px-8 py-3 font-bold text-white shadow-2xl shadow-green-500/30 transition-all hover:from-green-700 hover:via-emerald-700 hover:to-teal-700 disabled:opacity-50 disabled:hover:scale-100"
                >
                  <span className="relative z-10 flex items-center gap-3">
                    {saving ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="h-5 w-5 rounded-full border-2 border-white border-t-transparent"
                        />
                        Activating Security...
                      </>
                    ) : (
                      <>
                        <HiShieldCheck className="h-5 w-5" />
                        Complete Setup & Enable 2FA
                        <svg
                          className="h-5 w-5 transition-transform group-hover:translate-x-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M13 7l5 5m0 0l-5 5m5-5H6"
                          />
                        </svg>
                      </>
                    )}
                  </span>
                </motion.button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmation
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDisable2FA}
          title="Disable Two-Factor Authentication"
          message="Are you sure you want to disable two-factor authentication? This will make your account less secure."
        />

        {/* Enable OTP Confirmation Modal */}
        <AnimatePresence>
          {showEnableOtpConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              onClick={() => setShowEnableOtpConfirm(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={e => e.stopPropagation()}
                className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-indigo-100">
                    <HiShieldCheck className="h-8 w-8 text-indigo-600" />
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-slate-900">Enable Login OTP</h3>
                  <p className="mb-6 text-sm text-slate-600">
                    A one-time verification code will be sent to your email after each sign-in. This
                    adds an extra security layer to your account.
                  </p>
                  <div className="flex w-full gap-3">
                    <button
                      type="button"
                      onClick={() => setShowEnableOtpConfirm(false)}
                      className="flex-1 rounded-xl border-2 border-slate-300 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmEnableOtp}
                      disabled={otpSaving}
                      className="flex-1 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 py-3 font-semibold text-white shadow-lg transition-all hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50"
                    >
                      {otpSaving ? 'Enabling…' : 'Enable OTP'}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
